import { motion } from "framer-motion";
import { Mail, Monitor, Trophy, Sparkles, ExternalLink } from "lucide-react";
import { useEffect } from "react";
import bundleImg from "@/assets/bundle-completo-universo.png";
import logoImg from "@/assets/logo-universo-digital-64.webp";
import { useHotmartIframeFix } from "@/hooks/use-hotmart-iframe-fix";
import LazyVideo from "@/components/landing/LazyVideo";

const ThankYou = () => {
  useHotmartIframeFix("hotmart-sales-funnel", {
    minHeightDesktop: 260,
    maxHeightDesktop: 300,
    viewportFillDesktop: 0.28,
    minHeightMobile: 260,
    maxHeightMobile: 300,
    viewportFillMobile: 0.32,
  });

  // Load Hotmart Sales Funnel Widget
  useEffect(() => {
    const widgetContainer = document.getElementById('hotmart-sales-funnel');
    if (widgetContainer) {
      widgetContainer.innerHTML = '';
    }

    const script = document.createElement("script");
    script.src = "https://checkout.hotmart.com/lib/hotmart-checkout-elements.js";
    script.async = true;
    script.onload = () => {
      if ((window as any).checkoutElements) {
        (window as any).checkoutElements.init("salesFunnel").mount("#hotmart-sales-funnel");
      }
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount
      const existingScript = document.querySelector('script[src="https://checkout.hotmart.com/lib/hotmart-checkout-elements.js"]');
      if (existingScript) {
        existingScript.remove();
      }
      const container = document.getElementById('hotmart-sales-funnel');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);
  const steps = [
    {
      icon: Mail,
      title: "Paso 1: Revisa tu correo",
      description: "Hotmart te ha enviado tus credenciales de acceso inmediato.",
    },
    {
      icon: Monitor,
      title: "Paso 2: Accede al material",
      description: "Podrás disfrutar de todo el contenido desde cualquier dispositivo.",
    },
    {
      icon: Trophy,
      title: "Paso 3: Domina tu futuro",
      description: "Dentro tienes las herramientas exactas para transformar tu score hoy mismo.",
    },
  ];

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, ease: "easeOut" }}
      className="min-h-screen stars-bg text-white"
    >
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-20">
        
        {/* HERO Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-wide mb-6"
            style={{
              background: "linear-gradient(135deg, #d4af37 0%, #f5e6a3 50%, #d4af37 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            ¡Gracias por tu confianza! ✨💎
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Acabas de dar el paso más importante hacia tu libertad financiera y el dominio de tu crédito.
          </p>
        </motion.section>

        {/* Video Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mb-16"
        >
          <div className="relative max-w-sm mx-auto">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#3b82f6]/30 via-[#3b82f6]/10 to-[#3b82f6]/30 rounded-2xl blur-xl opacity-60" />
            <div className="relative bg-[#111111] rounded-2xl border border-white/10 overflow-hidden">
              <LazyVideo
                className="w-full aspect-[9/16]"
                poster="/images/video-poster-thankyou.jpg"
                src="/videos/video-thankyou.mp4"
              />
            </div>
          </div>
        </motion.section>

        {/* Product Mockups */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="flex items-center justify-center mb-16"
        >
          <div className="relative group">
            <div className="absolute -inset-4 bg-gradient-to-r from-[#d4af37]/20 via-[#3b82f6]/15 to-[#d4af37]/20 rounded-3xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity" />
            <img
              src={bundleImg}
              alt="Bundle Completo Universo Digital Academy"
              className="relative w-full max-w-[200px] md:max-w-xs rounded-2xl shadow-2xl"
              loading="lazy"
              decoding="async"
            />
          </div>
        </motion.section>

        {/* Main Message */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="bg-black border border-white/10 rounded-2xl p-6 md:p-10 max-w-3xl mx-auto">
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Estamos muy felices de tenerte en <span className="text-[#d4af37] font-semibold">Universo Digital Academy</span>. 
              Gracias por permitirnos ser parte de tu camino hacia el éxito crediticio. 
              Tu acceso ya está siendo procesado y llegará en los próximos minutos a tu correo. 
              <span className="text-[#3b82f6]"> Revisa también tu carpeta de Spam por si acaso.</span>
            </p>
          </div>
        </motion.section>

        {/* Steps Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-2xl md:text-3xl font-semibold text-center text-white mb-10">
            ¿Qué sigue ahora?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1, duration: 0.6 }}
                className="relative group"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-[#3b82f6]/30 to-[#3b82f6]/10 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-black border border-[#3b82f6]/20 rounded-2xl p-6 h-full text-center hover:border-[#3b82f6]/40 transition-colors duration-300">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6]/30 flex items-center justify-center">
                    <step.icon className="w-7 h-7 text-[#3b82f6]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Motivational Message */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="max-w-2xl mx-auto">
            <Sparkles className="w-8 h-8 text-[#d4af37] mx-auto mb-4" />
            <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed italic">
              "Cree en ti. El sistema financiero ahora trabajará para ti. Estamos orgullosos de tu compromiso con tu crecimiento."
            </p>
            <p className="text-[#d4af37] mt-4 text-lg">✨💎</p>
          </div>
        </motion.section>


        {/* Support Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="text-center mb-12"
        >
          <h3 className="text-xl font-semibold text-white mb-3">¿Necesitas ayuda?</h3>
          <p className="text-gray-400 mb-4">
            Estamos aquí para acompañarte. Si tienes dudas, escríbenos a nuestro equipo de soporte.
          </p>
          <a
            href="mailto:soporte@universodigitalacademy.com"
            className="inline-flex items-center gap-2 text-[#3b82f6] hover:text-[#60a5fa] transition-colors"
          >
            <Mail className="w-4 h-4" />
            soporte@universodigitalacademy.com
          </a>
        </motion.section>

        {/* CTA Button */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="text-center mb-16"
        >
          <a
            href="https://go.hotmart.com/member"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-4 rounded-full font-bold text-black text-sm sm:text-lg tracking-wide shadow-2xl hover:scale-105 transition-transform duration-300 text-center whitespace-normal"
            style={{
              background: "linear-gradient(135deg, #d4af37 0%, #f5e6a3 50%, #d4af37 100%)",
              boxShadow: "0 0 40px rgba(212, 175, 55, 0.4)",
            }}
          >
            <span>ACCEDER A MIS SISTEMAS AHORA</span>
            <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          </a>
        </motion.section>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="text-center pt-8 border-t border-white/5"
        >
          <img 
            src={logoImg} 
            alt="Universo Digital Academy" 
            className="w-24 md:w-32 mx-auto mb-4"
            width="128"
            height="128"
            loading="lazy"
            decoding="async"
          />
          <p className="text-gray-300 text-base leading-relaxed">
            Bienvenido a la familia de <span className="text-[#d4af37] font-medium">Universo Digital Academy</span>.
          </p>
          <p className="text-gray-400 text-base mt-2 mb-6">
            Transformando el futuro, un score a la vez. 🤍✨
          </p>
        </motion.footer>

        {/* Hotmart Sales Funnel Widget (full-bleed) */}
        <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
          <div id="hotmart-sales-funnel" className="w-full" />
        </div>
      </div>
    </motion.main>
  );
};

export default ThankYou;
